function toNumber(value) {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

export function roundTo(value, places = 2) {
  const number = toNumber(value);
  if (!Number.isFinite(number)) return 0;

  // Use exponent shifting to avoid binary floating-point edge cases like
  // 40.495 becoming 40.49 instead of 40.5 when rounding to 2 decimals.
  return Number(Math.round(`${number}e${places}`) + `e-${places}`);
}

function applyTradeRound(value, roundType = 0) {
  const number = toNumber(value);
  switch (roundType) {
    case 1:
      return Math.ceil(number);
    case 2:
      return Math.floor(number);
    default:
      return Math.round(number);
  }
}

function applyDetailRound(value, roundType = 0, decimalPlaces = 2) {
  const number = toNumber(value);
  switch (roundType) {
    case 1:
      return Math.round(number);
    case 2:
      return Math.ceil(number);
    case 3:
      return Math.floor(number);
    default:
      return roundTo(number, decimalPlaces);
  }
}

function normalizeOptions(options = {}) {
  return {
    trade: options.trade || { TaxType: 1, IsTaxAddition: false },
    tradeDet: options.tradeDet || null,
    tradeDets: Array.isArray(options.tradeDets) ? options.tradeDets : [],
    editedField: options.editedField || "Qty",
    roundType: options.roundType ?? 0,
    detailRoundType: options.detailRoundType ?? 0,
    detailTaxRoundType: options.detailTaxRoundType ?? 0,
    decimalPlaces: options.decimalPlaces ?? 2,
  };
}

function isTaxableTrade(trade) {
  return Number(trade?.TaxType) === 1;
}

function isTaxAdditionTrade(trade) {
  return !!trade?.IsTaxAddition;
}

function getBaseField(trade) {
  return isTaxAdditionTrade(trade) ? "YTax" : "Total";
}

function getRoundedTotal(value, options) {
  return applyDetailRound(
    value,
    options.detailRoundType,
    options.decimalPlaces
  );
}

function getRoundedYTax(value, options) {
  return applyDetailRound(
    value,
    options.detailTaxRoundType,
    options.decimalPlaces
  );
}

function getTaxPriceFromYTax(yTax, trade) {
  return isTaxableTrade(trade) ? Math.round(toNumber(yTax) * 0.05) : 0;
}

function getTaxPriceFromTotal(total, trade) {
  return isTaxableTrade(trade)
    ? Math.round((toNumber(total) * 0.05) / 1.05)
    : 0;
}

function syncUnitPrices(tradeDet, decimalPlaces) {
  tradeDet.SalPrice = roundTo(
    tradeDet.Qty ? toNumber(tradeDet.Total) / toNumber(tradeDet.Qty) : 0,
    decimalPlaces
  );
  tradeDet.UntaxedPrice = roundTo(
    tradeDet.Qty ? toNumber(tradeDet.YTax) / toNumber(tradeDet.Qty) : 0,
    decimalPlaces
  );
}

function initializeTradeDet(tradeDet) {
  if (!tradeDet) return;

  tradeDet.Qty = toNumber(tradeDet.Qty);
  if (tradeDet.Qty === 1) tradeDet.Qty = 1;
  tradeDet.OriginalPrice = toNumber(tradeDet.OriginalPrice);
  tradeDet.Total = toNumber(tradeDet.Total);
  tradeDet.YTax = toNumber(tradeDet.YTax);
  tradeDet.TaxPrice = toNumber(tradeDet.TaxPrice);
  tradeDet.SalPrice = toNumber(tradeDet.SalPrice);
  tradeDet.UntaxedPrice = toNumber(tradeDet.UntaxedPrice);
  tradeDet.TotalAdjustment = 0;
  tradeDet.TaxAdjustment = 0;
}

function restoreMissingDetailFields(
  tradeDet,
  trade,
  options,
  originalValues = {}
) {
  const hasOriginalTotal = hasValue(originalValues.Total);
  const hasOriginalYTax = hasValue(originalValues.YTax);
  const hasOriginalTaxPrice = hasValue(originalValues.TaxPrice);

  if (getBaseField(trade) === "YTax" && !hasOriginalYTax && hasOriginalTotal) {
    const total = toNumber(originalValues.Total);
    const taxPrice = hasOriginalTaxPrice
      ? toNumber(originalValues.TaxPrice)
      : isTaxableTrade(trade)
      ? getTaxPriceFromTotal(total, trade)
      : 0;

    tradeDet.TaxPrice = taxPrice;
    tradeDet.YTax = getRoundedYTax(total - taxPrice, options);
    return;
  }

  if (getBaseField(trade) === "Total" && !hasOriginalTotal && hasOriginalYTax) {
    const yTax = toNumber(originalValues.YTax);
    const taxPrice = hasOriginalTaxPrice
      ? toNumber(originalValues.TaxPrice)
      : getTaxPriceFromYTax(yTax, trade);

    tradeDet.TaxPrice = taxPrice;
    tradeDet.Total = getRoundedTotal(yTax + taxPrice, options);
  }
}

function resetTradeSummary(trade) {
  trade.Total = 0;
  trade.TaxPrice = 0;
  trade.YTax = 0;
  trade.YTaxPrice = 0;
  trade.ZTaxPrice = 0;
  trade.NTaxPrice = 0;
  trade.TotalAdjustment = 0;
  trade.TaxAdjustment = 0;
}

function assignTradeTaxSummary(trade) {
  trade.YTaxPrice = 0;
  trade.ZTaxPrice = 0;
  trade.NTaxPrice = 0;

  switch (Number(trade?.TaxType)) {
    case 1:
      trade.YTaxPrice = toNumber(trade.YTax);
      break;
    case 2:
      trade.ZTaxPrice = toNumber(trade.Total);
      break;
    case 3:
      trade.NTaxPrice = toNumber(trade.Total);
      break;
  }
}

function distributeAdjustment(tradeDets, fieldName, adjustmentFieldName, diff) {
  tradeDets.forEach((det) => {
    det[adjustmentFieldName] = 0;
  });

  let balance = toNumber(diff);
  if (balance === 0) return;

  for (const det of tradeDets) {
    if (balance === 0) break;

    const currentValue = toNumber(det[fieldName]);
    const nextValue = currentValue + balance;

    if (nextValue >= 0) {
      det[adjustmentFieldName] += balance;
      balance = 0;
    } else {
      det[adjustmentFieldName] -= currentValue;
      balance = nextValue;
    }
  }
}

function adjustTaxRounding(trade, tradeDets) {
  distributeAdjustment(
    tradeDets,
    "TaxPrice",
    "TaxAdjustment",
    trade.TaxAdjustment
  );

  tradeDets.forEach((det) => {
    det.TaxPrice = toNumber(det.TaxPrice) + toNumber(det.TaxAdjustment);
  });
}

function getRawDetailBaseValue(tradeDet, trade, editedField, decimalPlaces) {
  if (editedField === "Qty" || editedField === "OriginalPrice") {
    return toNumber(tradeDet.Qty) * toNumber(tradeDet.OriginalPrice);
  }

  if (editedField === "YTax") {
    tradeDet.OriginalPrice = roundTo(
      tradeDet.Qty ? toNumber(tradeDet.YTax) / toNumber(tradeDet.Qty) : 0,
      decimalPlaces
    );
    return toNumber(tradeDet.YTax);
  }

  if (editedField === "Total") {
    tradeDet.OriginalPrice = roundTo(
      tradeDet.Qty ? toNumber(tradeDet.Total) / toNumber(tradeDet.Qty) : 0,
      decimalPlaces
    );

    if (isTaxAdditionTrade(trade) && isTaxableTrade(trade)) {
      return toNumber(tradeDet.Total) / 1.05;
    }

    return toNumber(tradeDet.Total);
  }

  return null;
}

function assignDetailAmountsByBaseValue(tradeDet, trade, options, baseValue) {
  if (getBaseField(trade) === "YTax") {
    tradeDet.YTax = getRoundedYTax(baseValue, options);
    tradeDet.TaxPrice = getTaxPriceFromYTax(tradeDet.YTax, trade);
    tradeDet.Total = getRoundedTotal(
      toNumber(tradeDet.YTax) + toNumber(tradeDet.TaxPrice),
      options
    );
    return;
  }

  tradeDet.Total = getRoundedTotal(baseValue, options);
  tradeDet.TaxPrice = getTaxPriceFromTotal(tradeDet.Total, trade);
  tradeDet.YTax = getRoundedYTax(
    toNumber(tradeDet.Total) - toNumber(tradeDet.TaxPrice),
    options
  );
}

function syncTradeDetFields(tradeDet, trade, options) {
  const rawBaseValue = getRawDetailBaseValue(
    tradeDet,
    trade,
    options.editedField,
    options.decimalPlaces
  );

  if (rawBaseValue === null) return;

  tradeDet.TotalAdjustment = 0;
  tradeDet.TaxAdjustment = 0;
  assignDetailAmountsByBaseValue(tradeDet, trade, options, rawBaseValue);
  syncUnitPrices(tradeDet, options.decimalPlaces);
}

function finalizeTradeDet(trade, tradeDet, options) {
  if (getBaseField(trade) === "YTax") {
    tradeDet.Total = getRoundedTotal(
      toNumber(tradeDet.YTax) + toNumber(tradeDet.TaxPrice),
      options
    );
  } else {
    tradeDet.YTax = getRoundedYTax(
      toNumber(tradeDet.Total) - toNumber(tradeDet.TaxPrice),
      options
    );
  }

  syncUnitPrices(tradeDet, options.decimalPlaces);
}

export function calculateTradeDet(options = {}) {
  const normalized = normalizeOptions(options);
  const { tradeDet, trade } = normalized;

  if (!tradeDet) return;

  const originalValues = {
    Total: tradeDet.Total,
    YTax: tradeDet.YTax,
    TaxPrice: tradeDet.TaxPrice,
  };
  initializeTradeDet(tradeDet);
  restoreMissingDetailFields(tradeDet, trade, normalized, originalValues);
  syncTradeDetFields(tradeDet, trade, normalized);
}

export function calculateTrade(options = {}) {
  const normalized = normalizeOptions(options);
  const { trade, tradeDets } = normalized;

  resetTradeSummary(trade);

  if (!Array.isArray(tradeDets) || tradeDets.length === 0) {
    assignTradeTaxSummary(trade);
    return;
  }

  let aggregateBase = 0;
  let aggregateTax = 0;

  tradeDets.forEach((tradeDet) => {
    const originalValues = {
      Total: tradeDet.Total,
      YTax: tradeDet.YTax,
      TaxPrice: tradeDet.TaxPrice,
    };
    initializeTradeDet(tradeDet);
    restoreMissingDetailFields(tradeDet, trade, normalized, originalValues);

    if (getBaseField(trade) === "YTax") {
      tradeDet.TaxPrice = getTaxPriceFromYTax(tradeDet.YTax, trade);
      tradeDet.Total = toNumber(tradeDet.YTax) + toNumber(tradeDet.TaxPrice);
    } else {
      tradeDet.TaxPrice = getTaxPriceFromTotal(tradeDet.Total, trade);
      tradeDet.YTax = toNumber(tradeDet.Total) - toNumber(tradeDet.TaxPrice);
    }

    aggregateBase += toNumber(tradeDet[getBaseField(trade)]);
    aggregateTax += toNumber(tradeDet.TaxPrice);
  });

  const roundedBase = applyTradeRound(aggregateBase, normalized.roundType);

  trade.TotalAdjustment = roundedBase - aggregateBase;
  distributeAdjustment(
    tradeDets,
    getBaseField(trade),
    "TotalAdjustment",
    trade.TotalAdjustment
  );

  if (getBaseField(trade) === "YTax") {
    trade.YTax = roundedBase;
    trade.TaxPrice = getTaxPriceFromYTax(trade.YTax, trade);
    trade.Total = trade.YTax + trade.TaxPrice;
  } else {
    trade.Total = roundedBase;
    trade.TaxPrice = getTaxPriceFromTotal(trade.Total, trade);
    trade.YTax = trade.Total - trade.TaxPrice;
  }

  trade.TaxAdjustment = trade.TaxPrice - aggregateTax;
  adjustTaxRounding(trade, tradeDets);

  tradeDets.forEach((tradeDet) => {
    finalizeTradeDet(trade, tradeDet, normalized);
  });

  assignTradeTaxSummary(trade);
}

export function rebuildTradeDetOnTaxAdditionChange(options = {}) {
  const normalized = normalizeOptions(options);
  const { tradeDet } = normalized;

  if (!tradeDet) return;

  calculateTradeDet({
    ...normalized,
    editedField: "OriginalPrice",
  });
}

export function rebuildTradeDetOnTaxTypeChange(options = {}) {
  const normalized = normalizeOptions(options);
  const { tradeDet } = normalized;

  if (!tradeDet) return;

  calculateTradeDet({
    ...normalized,
    editedField: "OriginalPrice",
  });
}
